/**
 * PORTED (REWRITTEN) — RO-3-00d / #44471.
 * Origin: `excellent/app/projects/[projectSlug]/[sectionSlug]/_crm/renewal-view.tsx`.
 *
 * A REWRITE. The Next original called `customerParties(workspaceId)` — the
 * account-rolled-up read model, a vault read — inside its render path
 * (`shared-render-ownership.md` cause **A**, 171 own LOC + 208 delegate). That
 * read and the "is this a renewal subject" filter moved behind
 * `crm.views.renewal`; the three derived stat cells and the whole column set are
 * verbatim.
 *
 * `crm.views.renewal` is NOT a duplicate of `customers.portfolio`. Portfolio is
 * the entire account-rolled-up base; this is the RETENTION BOOK — the
 * `stage !== "prospect" || lifetimeMinor > 0` subset. It reuses the very same
 * `customerPartyDTO` schema (exported from `contracts/customers.ts` for this
 * purpose), which is also what `@excellent/ui/blocks/crm/customer-layers` is
 * typed against, so the two ops stay structurally interchangeable and the intent
 * maps cannot drift. `customers.portfolio` is in any case not mounted on this
 * host (no `customers` namespace exists under `excellent/server/routes/`).
 *
 * Changes, and only these:
 *   `import "server-only"`                     -> dropped (this host has no vault)
 *   `customerParties(...)` + the customer filter -> `useApi(crm.views.renewal, {})`
 *   `CustomerParty` (core type)                 -> `CustomerPartyDTO` (contract type)
 *
 * The contract IS mounted on this host — this component is live, not a stub.
 */

import {
  DataTable,
  StatStrip,
  IntentBadge,
  IntentDot,
  EmptyState,
  formatUsd,
  formatRelativeTime,
  objectHref,
  type StatStripItem,
  type DataTableColumn,
} from "@excellent/ui";
import { Repeat } from "@excellent/ui/icons";
import {
  CUSTOMER_STAGE_INTENT,
  CUSTOMER_STAGE_LABEL,
  CUSTOMER_BAND_INTENT,
  CUSTOMER_BAND_LABEL,
} from "@excellent/ui/blocks/crm/customer-layers";
import { useApi } from "@excellent/ui/api";
import { crm } from "@excellent/ui/api/contracts/crm";
import type { CustomerPartyDTO } from "@excellent/ui/api/contracts/customers";

/** Risk-first sort order for the default view (churn first — the decision-relevant end). */
const STAGE_RISK: Record<CustomerPartyDTO["stage"], number> = {
  churned: 0,
  at_risk: 1,
  active: 2,
  prospect: 3,
};

/**
 * Customers ▸ Renewal — the RETENTION book (the customer-lifecycle Renewal stage).
 * Renewal is the post-sale recurring-revenue motion, distinct from new-logo Sales
 * (a renewal is retention/expansion, owned by CS/AM — NOT the acquisition funnel),
 * so this is its own section, not a Sales tab. A REBUILD like Support: it DERIVES
 * from the `customerParties` read model (the same account-rolled-up base the
 * Portfolio uses) rather than faking a renewals-pipeline schema — there is no
 * contract-term / renewal-date field yet. It lists the accounts that are ALREADY
 * customers (won: past prospect, or with real lifetime revenue) with their
 * recurring revenue, health, and churn risk — the "who's slipping / who's up for
 * renewal" read. Row → the account dossier.
 *
 * `basePath` is accepted for the §5.5 view contract but Renewal is
 * workspace-global (the customer base has no project scope).
 */
export function RenewalView(_props: { basePath: string }) {
  const { data, error, isLoading } = useApi(crm.views.renewal, {});
  // Already the customer subset — the endpoint applies the same
  // `stage !== "prospect" || lifetimeMinor > 0` filter the server component did.
  const customers = data?.customers ?? [];

  const atRisk = customers.filter(
    (p) => p.stage === "at_risk" || p.stage === "churned",
  );
  const recurringMinor = customers.reduce((n, p) => n + p.ltv.lifetimeMinor, 0);
  const paying = customers.filter((p) => p.ltv.lifetimeMinor > 0).length;
  const atRiskPct = customers.length
    ? Math.round((atRisk.length / customers.length) * 100)
    : 0;

  // Three honest cells — no MRR read exists, so recurring revenue is lifetime value
  // (real transactions), the same honest stand-in the Portfolio uses.
  const stats: StatStripItem[] = [
    { title: "Customers", value: String(customers.length) },
    {
      title: "Recurring revenue",
      value: formatUsd(recurringMinor / 100),
      description: paying === 1 ? "1 paying account" : `${paying} paying accounts`,
    },
    {
      title: "At risk",
      value: String(atRisk.length),
      description: atRisk.length ? `${atRiskPct}% of the book` : "book is healthy",
    },
  ];

  const columns: DataTableColumn<CustomerPartyDTO>[] = [
    {
      key: "account",
      header: "Account",
      width: "w-4/12",
      render: (p) => (
        <span className="flex items-center gap-2">
          <IntentDot
            intent={CUSTOMER_BAND_INTENT[p.health.band]}
            size="sm"
            title={CUSTOMER_BAND_LABEL[p.health.band]}
          />
          <span className="truncate font-medium text-foreground">{p.name}</span>
        </span>
      ),
      sort: (a, b) => a.name.localeCompare(b.name),
    },
    {
      key: "status",
      header: "Status",
      width: "w-2/12",
      render: (p) => (
        <IntentBadge intent={CUSTOMER_STAGE_INTENT[p.stage]}>
          {CUSTOMER_STAGE_LABEL[p.stage]}
        </IntentBadge>
      ),
      sort: (a, b) => STAGE_RISK[a.stage] - STAGE_RISK[b.stage],
    },
    {
      key: "revenue",
      header: "Recurring revenue",
      width: "w-2/12",
      render: (p) =>
        p.ltv.lifetimeMinor > 0 ? (
          formatUsd(p.ltv.lifetimeMinor / 100)
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
      sort: (a, b) => a.ltv.lifetimeMinor - b.ltv.lifetimeMinor,
      defaultDesc: true,
    },
    {
      key: "lastpay",
      header: "Last payment",
      width: "w-2/12",
      render: (p) => (
        <span className="text-muted-foreground">
          {p.ltv.lastTxnAt ? formatRelativeTime(p.ltv.lastTxnAt) : "—"}
        </span>
      ),
      sort: (a, b) => (a.ltv.lastTxnAt ?? "").localeCompare(b.ltv.lastTxnAt ?? ""),
      defaultDesc: true,
    },
    {
      key: "touch",
      header: "Last touch",
      width: "w-2/12",
      render: (p) => (
        <span className="text-muted-foreground">
          {p.lastTouchAt ? formatRelativeTime(p.lastTouchAt) : "—"}
        </span>
      ),
      sort: (a, b) => (a.lastTouchAt ?? "").localeCompare(b.lastTouchAt ?? ""),
      defaultDesc: true,
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      {error ? (
        <p className="type-body text-destructive">{error.message ?? String(error)}</p>
      ) : null}
      <StatStrip items={stats} variant="standalone" />
      {isLoading && !data ? (
        <p className="type-body text-muted-foreground">Loading…</p>
      ) : customers.length === 0 ? (
        <EmptyState
          icon={<Repeat className="size-5" />}
          title="No customers to renew yet"
          description="Renewal tracks your existing customers' recurring revenue and churn risk. Win a deal in Sales — the account shows up here with its retention health."
        />
      ) : (
        <DataTable
          rows={customers}
          rowKey={(p) => p.uid}
          columns={columns}
          initialSort={{ key: "status", dir: "asc" }}
          rowHref={(p) => objectHref(p.uid, "customers")}
          empty="No customers match."
        />
      )}
    </div>
  );
}
